import * as React from "react";
import { cn } from "@/lib/utils";

interface CompassLogoProps extends React.ImgHTMLAttributes<HTMLImageElement> {
    /** Size of the container, defaults to 'size-6' */
    containerClassName?: string;
    /** Whether to show the animation effects */
    showAnimation?: boolean;
    /** Size variant for animations: 'sm' for small icons, 'lg' for larger displays */
    size?: "sm" | "lg";
}

const CompassLogo = React.forwardRef<HTMLDivElement, CompassLogoProps>(
    (
        {
            className,
            containerClassName,
            showAnimation = true,
            size = "sm",
            src = "../../assets/compass.png",
            alt = "Compass",
            ...imgProps
        },
        ref
    ) => {
        const rippleSizes = {
            sm: {
                inner: "w-1.5 h-1.5 border-2",
                middle: "w-3 h-3 border",
                outer: "w-[18px] h-[18px] border",
            },
            lg: {
                inner: "w-6 h-6 border-2",
                middle: "w-12 h-12 border-2",
                outer: "w-20 h-20 border",
            },
        };

        const ripple = rippleSizes[size];

        return (
            <div
                ref={ref}
                className={cn(
                    "relative inline-block overflow-hidden rounded-full size-6",
                    containerClassName
                )}
            >
                <img
                    src={src}
                    alt={alt}
                    className={cn(
                        "size-full rounded-full transition-transform duration-300 hover:scale-105 object-contain",
                        className
                    )}
                    {...imgProps}
                />
                {showAnimation && (
                    <>
                        <div className="absolute -top-1/2 -left-1/2 w-[200%] h-[200%] bg-[linear-gradient(45deg,transparent_30%,rgba(255,255,255,0.6)_50%,transparent_70%)] rounded-full pointer-events-none animate-sheen" />
                        <div className={cn("absolute top-1/2 left-1/2 border-[rgba(142,125,190,0.6)] rounded-full -translate-x-1/2 -translate-y-1/2 pointer-events-none animate-ripple", ripple.inner)} />
                        <div className={cn("absolute top-1/2 left-1/2 border-[rgba(142,125,190,0.4)] rounded-full -translate-x-1/2 -translate-y-1/2 pointer-events-none animate-ripple [animation-delay:0.5s]", ripple.middle)} />
                        <div className={cn("absolute top-1/2 left-1/2 border-[rgba(142,125,190,0.2)] rounded-full -translate-x-1/2 -translate-y-1/2 pointer-events-none animate-ripple [animation-delay:1s]", ripple.outer)} />
                    </>
                )}
            </div>
        );
    }
);

CompassLogo.displayName = "CompassLogo";

export default CompassLogo;
